import { UnauthorizedError } from "@/src/lib/error";
import { RBACRole, User } from "@/src/models/user";

/**
 * Permission types for different features
 */
export enum Permission {
  ANALYTICS_READ = "analytics:read",
  COMMS_INBOX_READ = "comms:inbox:read",
  COMMS_INBOX_WRITE = "comms:inbox:write",
  INTAKE_ANNOTATIONS_WRITE = "intake_annotations:write",
  ORDERS_READ = "orders:read",
  ORDERS_WRITE = "orders:write",
  PATIENT_WRITE = "patient:write",
  SOR_DELETE = "sor:delete",
  SOR_READ = "sor:read",
  SOR_WRITE = "sor:write",
  TASKS_READ = "tasks:read",
  TASKS_WRITE = "tasks:write",
  USERS_MANAGE = "users:manage",
}

const INTERNAL_DEFAULT_PERMISSIONS: Permission[] = [
  Permission.ANALYTICS_READ,
  Permission.COMMS_INBOX_READ,
  Permission.COMMS_INBOX_WRITE,
  Permission.INTAKE_ANNOTATIONS_WRITE,
  Permission.ORDERS_READ,
  Permission.ORDERS_WRITE,
  Permission.SOR_READ,
  Permission.SOR_WRITE,
  Permission.TASKS_READ,
  Permission.TASKS_WRITE,
];

/**
 * Role to permissions mapping
 */
const ROLE_PERMISSIONS: Record<RBACRole, Permission[]> = {
  [RBACRole.INTERNAL_ADMIN]: [...INTERNAL_DEFAULT_PERMISSIONS, Permission.SOR_DELETE],
  [RBACRole.INTERNAL_DEFAULT]: [...INTERNAL_DEFAULT_PERMISSIONS],
  // Tasks + full SOR R/W; no inbox, analytics, or orders.
  [RBACRole.INTERNAL_LIMITED]: [
    Permission.TASKS_READ,
    Permission.TASKS_WRITE,
    Permission.SOR_READ,
    Permission.SOR_WRITE,
  ],
  // external_analytics users get read-only access to the inbox (COMMS_INBOX_READ without
  // COMMS_INBOX_WRITE) — they can view conversations but cannot send messages or use inbox tools.
  [RBACRole.EXTERNAL_ANALYTICS]: [Permission.ANALYTICS_READ, Permission.COMMS_INBOX_READ, Permission.SOR_READ],
  // external_admin gets the same read access as external_analytics, plus USERS_MANAGE, and COMMS + SOR R/W access.
  [RBACRole.EXTERNAL_ADMIN]: [
    Permission.ANALYTICS_READ,
    Permission.COMMS_INBOX_READ,
    Permission.COMMS_INBOX_WRITE,
    Permission.SOR_READ,
    Permission.SOR_WRITE,
    Permission.USERS_MANAGE,
  ],
  // external_scheduling users can view and respond to patient scheduling conversations in the inbox
  // (read + write), plus read-only SOR access for patient context and patient create/update/delete
  // (PATIENT_WRITE) so they can register/edit patients while scheduling — but not full SOR write.
  [RBACRole.EXTERNAL_SCHEDULING]: [
    Permission.COMMS_INBOX_READ,
    Permission.COMMS_INBOX_WRITE,
    Permission.PATIENT_WRITE,
    Permission.SOR_READ,
  ],
  // external_analytics_readonly users get read-only analytics plus SOR read; no inbox or tasks.
  [RBACRole.EXTERNAL_ANALYTICS_READONLY]: [Permission.ANALYTICS_READ, Permission.SOR_READ],
  // external_orders users get read-only access to patient orders.
  [RBACRole.EXTERNAL_ORDERS]: [Permission.ORDERS_READ],
};

export const PATIENT_WRITE_PERMISSIONS = [Permission.PATIENT_WRITE, Permission.SOR_WRITE];

/**
 * Check if a user with given roles has a specific permission
 */
export function hasPermission(roles: string[], permission: Permission): boolean {
  for (const role of roles) {
    const roleEnum = role as RBACRole;
    const permissions = ROLE_PERMISSIONS[roleEnum];

    if (permissions && permissions.includes(permission)) {
      return true;
    }
  }

  return false;
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(roles: string[], permissions: Permission[]): boolean {
  return permissions.some((permission) => hasPermission(roles, permission));
}

/**
 * Check if user has all of the specified permissions
 */
export function hasAllPermissions(roles: string[], permissions: Permission[]): boolean {
  return permissions.every((permission) => hasPermission(roles, permission));
}

/**
 * Get all permissions for a set of roles
 */
export function getPermissions(roles: string[]): Permission[] {
  const allPermissions = new Set<Permission>();

  for (const role of roles) {
    const roleEnum = role as RBACRole;
    const permissions = ROLE_PERMISSIONS[roleEnum];

    if (permissions) {
      permissions.forEach((p) => allPermissions.add(p));
    }
  }

  return Array.from(allPermissions);
}

/**
 * Whether the member carries at least one recognized Valerie Health app role (any role that maps to
 * one or more permissions). Members with only Stytch's implicit `stytch_member` role — or any role
 * we don't recognize — map to zero permissions and must be denied access entirely (fail closed):
 * every provisioned role has at least `sor:read`, so an empty permission set means an un-provisioned
 * account with no business in the app. Enforced server-side in `authenticateRequest` and at the
 * front door in middleware (`src/proxy.ts`).
 */
export function hasAppAccess(roles: string[]): boolean {
  return getPermissions(roles).length > 0;
}

/**
 * Helper to check if user can access analytics
 */
export function canAccessAnalytics(roles: string[]): boolean {
  return hasPermission(roles, Permission.ANALYTICS_READ);
}

/**
 * Helper to check if user can access inbox/comms
 */
export function canAccessInbox(roles: string[]): boolean {
  return hasAnyPermission(roles, [Permission.COMMS_INBOX_READ, Permission.COMMS_INBOX_WRITE]);
}

/**
 * Helper to check if user can write to inbox/comms
 */
export function canWriteInbox(roles: string[]): boolean {
  return hasPermission(roles, Permission.COMMS_INBOX_WRITE);
}

/**
 * Helper to check if user can write intake annotations
 */
export function canWriteIntakeAnnotations(roles: string[]): boolean {
  return hasPermission(roles, Permission.INTAKE_ANNOTATIONS_WRITE);
}

/**
 * Helper to check if user can access SOR (patients, referrals, files, providers, locations)
 */
export function canAccessSOR(roles: string[]): boolean {
  return hasAnyPermission(roles, [Permission.SOR_READ, Permission.SOR_WRITE]);
}

/**
 * Helper to check if user can write to SOR
 */
export function canWriteSOR(roles: string[]): boolean {
  return hasPermission(roles, Permission.SOR_WRITE);
}

/**
 * Helper to check if user can write to patients
 */
export function canWritePatient(roles: string[]): boolean {
  return hasAnyPermission(roles, PATIENT_WRITE_PERMISSIONS);
}

/**
 * Helper to check if user can access tasks
 */
export function canAccessTasks(roles: string[]): boolean {
  return hasAnyPermission(roles, [Permission.TASKS_READ, Permission.TASKS_WRITE]);
}

/**
 * Helper to check if user can write to tasks
 */
export function canWriteTasks(roles: string[]): boolean {
  return hasPermission(roles, Permission.TASKS_WRITE);
}

/**
 * Helper to check if user can access patient orders
 */
export function canAccessOrders(roles: string[]): boolean {
  return hasAnyPermission(roles, [Permission.ORDERS_READ, Permission.ORDERS_WRITE]);
}

/**
 * Helper to check if user can mutate patient orders / workflow
 */
export function canWriteOrders(roles: string[]): boolean {
  return hasPermission(roles, Permission.ORDERS_WRITE);
}

/**
 * Helper to check if user can manage organization members (Permissions Management tool).
 */
export function canManageUsers(roles: string[]): boolean {
  return hasPermission(roles, Permission.USERS_MANAGE);
}

/**
 * Default set of roles a customer admin (USERS_MANAGE) may assign to members through the
 * Permissions Management tool. Internal (`internal_*`) roles are deliberately excluded: letting a
 * customer admin grant them would be privilege escalation. Per-group overrides live in the group's
 * own config (e.g. `ENTP_ASSIGNABLE_ROLES`); `getAssignableRolesForGroup` in `configuration/index.ts`
 * resolves the effective set by subdomain and falls back to this default.
 */
export const EXTERNALLY_ASSIGNABLE_ROLES: readonly RBACRole[] = [
  RBACRole.EXTERNAL_ANALYTICS,
  RBACRole.EXTERNAL_ADMIN,
  RBACRole.EXTERNAL_SCHEDULING,
];

/**
 * Whether a role id is an internal Valerie Health staff role (`internal_*`). Members carrying any
 * such role are hidden from — and unmanageable via — the customer-facing Permissions Management
 * tool. Prefix-based so it also covers internal roles not yet in the RBACRole enum.
 */
export function isInternalRole(role: string): boolean {
  return role.startsWith("internal_");
}

export const INTERNAL_EMAIL_DOMAINS = ["@valeriehealth.com", "@ext.valeriehealth.com"];

/** Whether an email address belongs to internal Valerie Health staff. */
export function isInternalEmail(email: string | null | undefined): boolean {
  const normalized = email?.toLowerCase().trim() ?? "";

  return INTERNAL_EMAIL_DOMAINS.some((domain) => normalized.endsWith(domain));
}

export function isExternalUser(roles: string[]): boolean {
  return !roles.some(isInternalRole);
}

/**
 * Shown to external customer users in place of an internal Valerie staffer's name, so staff
 * identities are never exposed to external tenants. Redaction happens server-side at the API
 * boundary, so external clients never receive internal names/emails in the first place.
 */
export const GENERIC_INTERNAL_USER_NAME = "Valerie Health";

// Blanks an internal staffer's identifying fields (name + email) while keeping the opaque id so
// assignment/lookup by id still works. Non-internal users are returned untouched.
function redactInternalUser(user: User): User {
  if (!isInternalEmail(user.email)) {
    return user;
  }

  return { ...user, givenName: GENERIC_INTERNAL_USER_NAME, familyName: "", email: "" };
}

/**
 * Whether a user has already been redacted to the generic "Valerie Health" identity by
 * {@link redactInternalUser} (name collapsed to the generic label, family name blanked). Because
 * every internal staffer redacts to the same indistinguishable identity, client UIs use this to
 * collapse the many duplicate "Valerie Health" rows down to a single one.
 */
export function isRedactedInternalUser(user: User): boolean {
  return user.givenName === GENERIC_INTERNAL_USER_NAME && user.familyName === "";
}

export function redactUserListForViewer(users: User[], viewerRoles: string[]): User[] {
  if (!isExternalUser(viewerRoles)) {
    return users;
  }

  return users.map(redactInternalUser);
}

export function redactAssignmentUsersForViewer(
  assignmentUsers: Record<string, User | null>,
  viewerRoles: string[]
): Record<string, User | null> {
  if (!isExternalUser(viewerRoles)) {
    return assignmentUsers;
  }

  return Object.fromEntries(
    Object.entries(assignmentUsers).map(([patientId, user]) => [patientId, user && redactInternalUser(user)])
  );
}

/**
 * Whether the user is internal Valerie staff (as opposed to an external customer).
 * Temporary guard for features not yet ready for external tenants.
 */
export function isInternalUser(roles: string[]): boolean {
  return (
    roles.includes(RBACRole.INTERNAL_ADMIN) ||
    roles.includes(RBACRole.INTERNAL_DEFAULT) ||
    roles.includes(RBACRole.INTERNAL_LIMITED)
  );
}

/**
 * Whether the user is an external inbox user (`external_scheduling` or `external_admin`). These users
 * work patient scheduling conversations in the inbox (they carry `comms:inbox:write` like internal
 * staff, so a permission check can't distinguish them), but must not see or drive the SMS agent's
 * tooling: the inbox scheduling Tools panel is hidden and the agent on/off toggle is read-only — they
 * can see the agent's status but not change it. They can still assign conversations to other users.
 */
export function isExternalInboxUser(roles: string[]): boolean {
  return (
    (roles.includes(RBACRole.EXTERNAL_SCHEDULING) || roles.includes(RBACRole.EXTERNAL_ADMIN)) && !isInternalUser(roles)
  );
}

/**
 * Get the default route for a user based on their roles
 */
export function getDefaultRoute(roles: string[]): string {
  if (roles.includes(RBACRole.EXTERNAL_ORDERS)) {
    return "/orders";
  }

  // Analytics/admin roles default to referrals. external_analytics, external_analytics_readonly,
  // and external_admin all have sor:read (referrals) but no tasks access, so /tasks would be a
  // denied landing (RBACGuard would bounce them back via this same function).
  if (
    roles.includes(RBACRole.EXTERNAL_ANALYTICS) ||
    roles.includes(RBACRole.EXTERNAL_ANALYTICS_READONLY) ||
    roles.includes(RBACRole.EXTERNAL_ADMIN) ||
    roles.includes(RBACRole.EXTERNAL_SCHEDULING)
  ) {
    return "/referrals";
  }

  // Default for internal_default or other roles
  return "/tasks";
}

/**
 * Check permission and throw error if not authorized
 * Use this in API routes to enforce RBAC
 */
export function requirePermission(roles: string[], permission: Permission): void {
  if (!hasPermission(roles, permission)) {
    throw new UnauthorizedError(`User does not have permission ${permission}`);
  }
}

/**
 * Check that the user holds at least one of the given permissions; throw otherwise. Use for
 * operations satisfiable by any of several permissions (e.g. patient writes accept PATIENT_WRITE or
 * the broader SOR_WRITE).
 */
export function requireAnyPermission(roles: string[], permissions: Permission[]): void {
  if (!hasAnyPermission(roles, permissions)) {
    throw new UnauthorizedError(`User does not have any of permissions: ${permissions.join(", ")}`);
  }
}
